import { Stack } from '@mantine/core';
import type { ComboboxItem } from '@mantine/core';
import type { FC } from 'react';
import type { UseFormReturn } from 'react-hook-form';

import { CheckboxSelect } from '../../../components/form/CheckboxSelect.tsx';
import { RichEditor } from '../../../components/form/RichEditor/RichEditor.tsx';
import { TagsSelect } from '../../../components/form/TagsSelect.tsx';
import { Select } from '../../../components/reactHookFormMantine/Select.tsx';
import { TextInput } from '../../../components/reactHookFormMantine/TextInput.tsx';
import { useCategoriesItemsQuery } from '../../../data/useCategoriesItemsQuery.ts';
import { useEventDetailsItemsQuery } from '../../../data/useEventDetailsItemsQuery.ts';
import { useTargetAudiencesItemsQuery } from '../../../data/useTargetAudiencesItemsQuery.ts';
import { useI18n } from '../../../hooks/useI18n.ts';
import { notEmpty } from '../../../lib/notEmpty.ts';
import type { IncompleteEventFormComplete } from '../IncompleteEventForm/schema.ts';

interface Item {
  title?: string | null;
  name?: string | null;
  id?: number | string;
  text?: string | null;
}

const parseDataToComboBox = (items?: Item[]): ComboboxItem[] => {
  if (!items) {
    return [];
  }

  return items
    .map((categoryItem) => {
      const label = categoryItem.name ?? categoryItem.title ?? categoryItem.text;

      if (!label || !categoryItem.id) {
        return null;
      }

      return { label, value: categoryItem.id.toString(10) };
    })
    .filter(notEmpty);
};

export const About: FC<{
  useFormValue: UseFormReturn<IncompleteEventFormComplete>;
}> = ({ useFormValue: { control } }) => {
  const { t } = useI18n();

  const { data: categoryItems } = useCategoriesItemsQuery();
  const { data: targetAudiencesItems } = useTargetAudiencesItemsQuery();
  const { data: detailsItems } = useEventDetailsItemsQuery();

  const parsedCategoryItems = parseDataToComboBox(categoryItems);
  const parsedTargetAudiencesItems = parseDataToComboBox(targetAudiencesItems);
  const parsedDetailsItems = parseDataToComboBox(detailsItems);

  return (
    <Stack gap="xs">
      <TextInput
        required
        control={control}
        label={t('event-form_field-label--event-name') ?? 'Event name'}
        name="name"
        placeholder={t('event-form_field-placeholder--event-name') ?? 'Event name'}
        type="input"
      />
      <RichEditor
        control={control}
        label={t('event-form_field-label--description') ?? 'Tell us more about your event'}
        name="description"
        placeholder={t('event-form_field-placeholder--description') ?? 'Add your event text here'}
      />
      <Select
        required
        control={control}
        data={parsedCategoryItems}
        label={t('event-form_field-label--category') ?? 'Category'}
        name="category"
        placeholder={t('event-form_field-placeholder--category') ?? 'Select a category'}
      />
      <TagsSelect
        control={control}
        data={parsedTargetAudiencesItems}
        label={t('event-form_field-label--tags') ?? 'Who is it for'}
        maxValues={3}
        name="tags"
      />
      <CheckboxSelect
        control={control}
        data={parsedDetailsItems}
        label={t('event-form_field-label--additional-details') ?? 'Additional Details'}
        name="eventDetails"
      />
    </Stack>
  );
};
